// src/components/institution/manager/ResourcesTab.jsx
import React, { useState, useEffect } from 'react';
import { 
  Plus, Search, Edit, Trash2, FileText, Download, Upload, Eye, 
  Folder, Filter, Package, BookOpen, Briefcase, Grid, List,
  Monitor, Chair, Shield, Video, Calendar, AlertTriangle, 
  CheckCircle, Clock, MapPin, DollarSign, ExternalLink,
  BarChart3, TrendingUp, Users, Image as ImageIcon, File,
  Paperclip, Camera
} from 'lucide-react';
import { institutionService } from '../../../services/institutionService';
import { ResourceForm } from '../forms/ResourceForm';

// Import resource type configurations
const RESOURCE_TYPES = {
  physical: {
    label: 'Physical Resources',
    icon: Package,
    color: 'blue',
    description: 'Institution\'s physical assets and equipment'
  },
  learning: {
    label: 'Learning Materials',
    icon: BookOpen,
    color: 'green',
    description: 'Educational content and teaching resources'
  },
  management: {
    label: 'Management Resources',
    icon: Briefcase,
    color: 'purple',
    description: 'Administrative and governance materials'
  }
};

const STATUS_CONFIG = {
  available: { label: 'Available', color: 'text-green-600 bg-green-100', icon: CheckCircle },
  inUse: { label: 'In Use', color: 'text-blue-600 bg-blue-100', icon: Clock },
  maintenance: { label: 'Maintenance', color: 'text-yellow-600 bg-yellow-100', icon: AlertTriangle },
  damaged: { label: 'Damaged', color: 'text-red-600 bg-red-100', icon: AlertTriangle },
  retired: { label: 'Retired', color: 'text-gray-600 bg-gray-100', icon: Package }
};

export const ResourcesTab = ({ bookId, institutionData, onUpdate }) => {
  const [resources, setResources] = useState([]);
  const [filteredResources, setFilteredResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [stats, setStats] = useState({});

  useEffect(() => {
    loadResources();
    loadStats();
  }, [bookId]);

  useEffect(() => {
    applyFilters();
  }, [resources, searchTerm, selectedType, selectedCategory, selectedStatus]);

  const loadResources = async () => {
    try {
      const resourcesData = await institutionService.getResources(bookId);
      setResources(resourcesData);
    } catch (error) {
      console.error('Error loading resources:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await institutionService.getResourceStats(bookId);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...resources];

    if (searchTerm) {
      filtered = filtered.filter(resource =>
        resource.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.assetId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (selectedType !== 'all') {
      filtered = filtered.filter(resource => resource.resourceType === selectedType);
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(resource => resource.category === selectedCategory);
    }

    if (selectedStatus !== 'all') {
      filtered = filtered.filter(resource => resource.status === selectedStatus);
    }

    filtered.sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));
    setFilteredResources(filtered);
  };

  const handleSaveResource = async (resourceData) => {
    try {
      if (editingResource) {
        await institutionService.updateResource(bookId, editingResource.id, resourceData);
      } else {
        await institutionService.createResource(bookId, resourceData);
      }
      
      await loadResources();
      await loadStats();
      await onUpdate();
      setShowForm(false);
      setEditingResource(null);
    } catch (error) {
      console.error('Error saving resource:', error);
      alert('Failed to save resource');
    }
  };

  const handleDeleteResource = async (resourceId) => {
    if (window.confirm('Are you sure you want to delete this resource?')) {
      try {
        await institutionService.deleteResource(bookId, resourceId);
        await loadResources();
        await loadStats();
        await onUpdate();
      } catch (error) {
        console.error('Error deleting resource:', error);
        alert('Failed to delete resource');
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round(bytes / Math.pow(1024, i))} ${sizes[i]}`;
  };

  const getResourceIcon = (resource) => {
    const typeConfig = RESOURCE_TYPES[resource.resourceType];
    return typeConfig?.icon || FileText;
  };

  const getFileTypeIcon = (fileType) => {
    if (fileType?.includes('image')) return ImageIcon;
    if (fileType?.includes('pdf')) return FileText;
    if (fileType?.includes('video')) return Video;
    return File;
  };

  const renderResourceCard = (resource) => {
    const IconComponent = getResourceIcon(resource);
    const typeConfig = RESOURCE_TYPES[resource.resourceType];
    const statusConfig = resource.status ? STATUS_CONFIG[resource.status] : null;
    const hasImages = resource.images && resource.images.length > 0;
    const hasDocuments = resource.documents && resource.documents.length > 0;

    return (
      <div 
        key={resource.id}
        className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-all"
      >
        {/* Image Preview Section */}
        {hasImages && (
          <div className="relative">
            <img
              src={resource.images[0].url}
              alt={resource.title}
              className="w-full h-32 object-cover"
            />
            {resource.images.length > 1 && (
              <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
                <Camera className="h-3 w-3" />
                {resource.images.length}
              </div>
            )}
          </div>
        )}

        <div className="p-4">
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className={`p-2 rounded-lg ${
                typeConfig?.color === 'blue' ? 'bg-blue-100 text-blue-600' :
                typeConfig?.color === 'green' ? 'bg-green-100 text-green-600' :
                'bg-purple-100 text-purple-600'
              }`}>
                <IconComponent className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-gray-900 truncate">
                  {resource.title}
                </h3>
                <p className="text-xs text-gray-500">
                  {typeConfig?.label} • {resource.category}
                </p>
              </div>
            </div>
            
            <div className="flex gap-1">
              <button
                onClick={() => {
                  setEditingResource(resource);
                  setShowForm(true);
                }}
                className="p-1 text-gray-400 hover:text-indigo-600"
              >
                <Edit className="h-4 w-4" />
              </button>
              
              <button
                onClick={() => handleDeleteResource(resource.id)}
                className="p-1 text-gray-400 hover:text-red-600"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Description */}
          {resource.description && (
            <p className="text-sm text-gray-600 mb-3 line-clamp-2">
              {resource.description}
            </p>
          )}

          {/* Resource Details */}
          <div className="space-y-2 mb-3">
            {/* Physical Resource Details */}
            {resource.resourceType === 'physical' && (
              <>
                {resource.assetId && (
                  <div className="flex items-center gap-2 text-xs">
                    <Package className="h-3 w-3 text-gray-400" />
                    <span className="text-gray-600">ID: {resource.assetId}</span>
                  </div>
                )}
                {resource.location && (
                  <div className="flex items-center gap-2 text-xs">
                    <MapPin className="h-3 w-3 text-gray-400" />
                    <span className="text-gray-600">{resource.location}</span>
                  </div>
                )}
                {resource.value && (
                  <div className="flex items-center gap-2 text-xs">
                    <DollarSign className="h-3 w-3 text-gray-400" />
                    <span className="text-gray-600">${resource.value}</span>
                  </div>
                )}
              </>
            )}

            {/* File Information */}
            <div className="flex items-center gap-4 text-xs">
              {hasImages && (
                <div className="flex items-center gap-1 text-blue-600">
                  <Camera className="h-3 w-3" />
                  <span>{resource.images.length} image{resource.images.length !== 1 ? 's' : ''}</span>
                </div>
              )}
              
              {hasDocuments && (
                <div className="flex items-center gap-1 text-green-600">
                  <Paperclip className="h-3 w-3" />
                  <span>{resource.documents.length} doc{resource.documents.length !== 1 ? 's' : ''}</span>
                </div>
              )}

              {resource.fileSize && (
                <div className="flex items-center gap-1 text-gray-600">
                  <FileText className="h-3 w-3" />
                  <span>{formatFileSize(resource.fileSize)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Status & Tags */}
          <div className="flex items-center justify-between">
            <div className="flex flex-wrap gap-1">
              {/* Status for Physical Resources */}
              {resource.resourceType === 'physical' && statusConfig && (
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusConfig.color}`}>
                  {statusConfig.label}
                </span>
              )}
              
              {/* Tags */}
              {resource.tags && resource.tags.length > 0 && (
                <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-md">
                  {resource.tags[0]}
                  {resource.tags.length > 1 && ` +${resource.tags.length - 1}`}
                </span>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex gap-1">
              {/* View Images */}
              {hasImages && (
                <button
                  onClick={() => window.open(resource.images[0].url, '_blank')}
                  className="p-1 text-gray-400 hover:text-blue-600"
                  title="View Images"
                >
                  <ImageIcon className="h-4 w-4" />
                </button>
              )}

              {/* Main File Actions */}
              {resource.fileUrl && (
                <button
                  onClick={() => window.open(resource.fileUrl, '_blank')}
                  className="p-1 text-gray-400 hover:text-blue-600"
                  title="View File"
                >
                  <Eye className="h-4 w-4" />
                </button>
              )}
              
              {resource.downloadUrl && (
                <button
                  onClick={() => window.open(resource.downloadUrl, '_blank')}
                  className="p-1 text-gray-400 hover:text-green-600"
                  title="Download"
                >
                  <Download className="h-4 w-4" />
                </button>
              )}

              {resource.externalUrl && (
                <button
                  onClick={() => window.open(resource.externalUrl, '_blank')}
                  className="p-1 text-gray-400 hover:text-blue-600"
                  title="Open Link"
                >
                  <ExternalLink className="h-4 w-4" />
                </button>
              )}

              {/* Documents Indicator */}
              {hasDocuments && (
                <button
                  className="p-1 text-gray-400 hover:text-green-600"
                  title={`${resource.documents.length} supporting documents`}
                >
                  <Paperclip className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const categories = [...new Set(resources.map(r => r.category).filter(Boolean))];

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <FileText className="h-6 w-6 text-indigo-600" />
            Resources Management
          </h2>
          <p className="text-gray-600">Manage all institution resources and assets</p>
        </div>

        {/* Stats Cards */}
        <div className="flex gap-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">{stats.physical || 0} Physical</span>
            </div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium text-green-900">{stats.learning || 0} Learning</span>
            </div>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-purple-600" />
              <span className="text-sm font-medium text-purple-900">{stats.management || 0} Management</span>
            </div>
          </div>
        </div>
      </div>

      {/* Controls - Same as before but shorter for brevity */}
      <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
        {/* Search */}
        <div className="flex flex-1 max-w-md">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search resources..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'grid' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Grid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'list' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <List className="h-4 w-4" />
            </button>
          </div>

          {/* Add Resource */}
          <button
            onClick={() => setShowForm(true)}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Resource
          </button>
        </div>
      </div>

      {/* Filters - Same as before */}
      <div className="flex flex-wrap gap-3">
        {/* Type Filter */}
        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
        >
          <option value="all">All Types</option>
          {Object.entries(RESOURCE_TYPES).map(([type, config]) => (
            <option key={type} value={type}>{config.label}</option>
          ))}
        </select>

        {/* Category Filter */}
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
        >
          <option value="all">All Categories</option>
          {categories.map(category => (
            <option key={category} value={category}>{category}</option>
          ))}
        </select>

        {/* Status Filter */}
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
        >
          <option value="all">All Status</option>
          {Object.entries(STATUS_CONFIG).map(([status, config]) => (
            <option key={status} value={status}>{config.label}</option>
          ))}
        </select>
      </div>

      {/* Resources Display */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : filteredResources.length > 0 ? (
        <>
          <div className="text-sm text-gray-600 mb-4">
            Showing {filteredResources.length} of {resources.length} resources
          </div>
          
          <div className={
            viewMode === 'grid' 
              ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              : "space-y-3"
          }>
            {filteredResources.map(renderResourceCard)}
          </div>
        </>
      ) : (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No resources found</h3>
          <p className="text-gray-600 mb-6">
            {searchTerm || selectedType !== 'all' || selectedCategory !== 'all'
              ? 'No resources match your current filters'
              : 'Get started by adding your first resource'
            }
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700"
          >
            Add Resource
          </button>
        </div>
      )}

      {/* Resource Form Modal */}
      {showForm && (
        <ResourceForm
          resource={editingResource}
          onSave={handleSaveResource}
          onClose={() => {
            setShowForm(false);
            setEditingResource(null);
          }}
          bookId={bookId}
        />
      )}
    </div>
  );
};